import type { ReactNode } from 'react';

import { AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-5 w-5 animate-spin text-primary', className)} aria-hidden="true" />;
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex animate-fade-in flex-col items-center justify-center gap-4 py-16"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-card bg-primary-100" aria-hidden="true">
        <Spinner className="h-6 w-6" />
      </span>
      <p className="text-caption text-gray-700">{label}</p>
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ title, description, icon, action }: EmptyStateProps) {
  return (
    <div className="flex animate-slide-up flex-col items-center justify-center gap-4 px-6 py-16 text-center">
      {icon && (
        <div
          className="flex h-14 w-14 items-center justify-center rounded-card bg-primary-100 text-primary"
          aria-hidden="true"
        >
          {icon}
        </div>
      )}
      <div>
        <p className="text-h2 text-gray-900">{title}</p>
        {description && <p className="mx-auto mt-1.5 max-w-prose text-base text-gray-700">{description}</p>}
      </div>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
}

export function ErrorState({ title = 'Something went wrong', message, onRetry }: ErrorStateProps) {
  return (
    <div role="alert" className="flex animate-fade-in flex-col items-center justify-center gap-4 px-6 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-card bg-burgundy-100 text-burgundy">
        <AlertTriangle className="h-6 w-6" aria-hidden="true" />
      </div>
      <div>
        <p className="text-h2 text-gray-900">{title}</p>
        <p className="mx-auto mt-1.5 max-w-prose text-base text-gray-700">{message}</p>
      </div>
      {onRetry && (
        <Button id="btn_TryAgain" variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function FormAlert({ id, message }: { id?: string; message: string }) {
  return (
    <div
      {...(id ? { id } : {})}
      role="alert"
      className="flex items-start gap-2.5 rounded-field border border-burgundy/25 bg-burgundy-100 px-4 py-3"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-burgundy" aria-hidden="true" />
      <p className="text-base text-burgundy-700">{message}</p>
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <span className={cn('block animate-pulse rounded-field bg-gray-100', className)} aria-hidden="true" />;
}

export interface SkeletonRowsProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export function SkeletonRows({ rows = 5, columns = 4, className }: SkeletonRowsProps) {
  return (
    <div role="status" aria-live="polite" aria-label="Loading rows" className={cn('divide-y divide-gray-300/40', className)}>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex items-center gap-4 px-6 py-4">
          {Array.from({ length: columns }, (_, column) => (
            <Skeleton key={column} className={cn('h-4', column === 0 ? 'flex-[2]' : 'flex-1')} />
          ))}
        </div>
      ))}
    </div>
  );
}
